// profiles.store.js
import { observable, computed, action, runInAction, makeObservable } from "mobx";
import { axios, authAxios } from "../../utils/axios";

// const { NotificationManager } = require("react-notifications");

import Profile from "./profiles/profile";
import { registrationErrorMessage } from "./registration-errors";

export default class ProfilesStore {
    @observable profiles: Profile[] = [];
    @observable profile: Profile;
    @observable status = "pending"; // 'pending' / 'done' / 'error'
    @observable redirectTo: string | undefined = undefined;
    @observable googleToken = "";
    @observable statusMessage = "";
    localStorage: any;
    rootStore: any;

    constructor(rootStore: any) {
        this.rootStore = rootStore;
        this.localStorage = window.localStorage;
        this.profile = new Profile("", 0, "", "", "");
        makeObservable(this);
    }

    @action.bound
    clearRedirect() {
        this.redirectTo = "";
    }

    newProfileObject(httpRes: any) {
        this.profile = new Profile(
            httpRes.email,
            httpRes.profile.age,
            httpRes.profile.name,
            httpRes.profile.username,
            httpRes.profile.quote,
        );
    }

    @action.bound
    fetchProfile() {
        if (!this.authTokenSet()) {
            return;
        }
        const store = this;
        this.status = "pending";

        // Need this to use AUTH_BASE_URL not BASE_URL, since profile is an authenticated route
        authAxios()
            .get("/profile")
            .then((res: any) => res.data)
            .then((res: any) => this.newProfileObject(res))
            .then(() => { store.status = "done"; })
            .catch((err: any) => {
                store.status = "error";
            });
    }


    processedErrors(err: any) {
        if (err.response.data.duplicate == "email") {
            return {
                type: "email_error",
                message: "Email Already Taken"
            }
        }
        if (err.response.data.duplicate == "username") {
            return {
                type: "username_error",
                message: "Username Already Taken"
            }
        }
        return err
    }

    confirmUsername(email: string, username: string) {
        const store = this;
        const auth_params = {
            email: email,
            username: username
        }
        return axios()
            .post("/unique_username_and_email", auth_params)
            .then((_es: any) => undefined)
            .catch((err: any) => store.processedErrors(err))
    }

    finishGoogleSignup(username: string, cb: any) {
        const store = this;
        const googleTokenBlob = {
            access_token: this.googleToken,
            username: username
        };

        this.statusMessage = "Finishing Google Signup";
        this.status        = "pending";
        axios()
            .post("/auth/google/signup", googleTokenBlob, {
                headers: { "Content-Type": "application/json" }
            })
            .then((res: any) => res.data)
            .then((res: any) => store.processUserResponse(res))
            .then((_res: any) => cb(false))
            .catch((err: any) => {
                if (err.response) {
                    console.log(err.response.data);
                } else {
                    store.statusMessage = "There was an error";
                    return;
                }
                store.rootStore.uiStore.fieldError.username = err.response.data;
                cb(true);
            }).finally(() => {
                store.googleToken = "";
                store.statusMessage = "User Successfully Registered";
            })
    }

    setUserJWT(token: string) {
        this.statusMessage = "Signing in";
        if (!token) {
            this.status = "error";
            this.statusMessage = "Sorry, couldn't sign in";
        }
        this.localStorage.setItem("user", token);
    }

    storeGoogleSignup(res: any) {
        this.googleToken = res.tokenId;
    }

    processGoogleLogin(res: any) {
        const store = this;

        this.statusMessage = "Starting google login";
        this.googleToken = res.tokenId;

        const googleTokenBlob = {
            access_token: this.googleToken
        };

        if (googleTokenBlob.access_token.length == 0) {
            this.status = "error";
            this.statusMessage =
                "Sorry, there was a problem logging in with google";
            return;
        }

        this.statusMessage = "Sending google authentication to server";
        return axios()
            .post("/auth/google/login", googleTokenBlob)
            .then((res: any) => res.data)
            .then((res: any) => {
                store.newProfileObject(res.user)
                store.loginWithToken(res.token)
            })
            .then((_es: any) => {
                store.googleToken = "";
                store.statusMessage = "User Successfully Registered"
            })
            .catch((err: any) => {
                if (err.response && err.response.status == 401) {
                    store.redirectTo = "/signup";
                    store.status = "warning";
                    store.statusMessage =
                        "Cannot find account matching your google user, please sign up for an account";
                    return;
                } else {
                    store.statusMessage = `There was an error logging into google: ${err}`;
                }
            });
    }

    async signUp(
        email: string,
        username: string,
        password: string,
        confirm_password: string,
        cb: Function
    ) {
        const store = this;

        if (password != confirm_password) {
            return cb(true, "password_error", "Passwords don't match");
        }

        const signup_params = { email, username, password };

        return authAxios()
            .post("/auth/register", signup_params)
            .then((res: any) => res.data)
            .then((res: any) => store.processUserResponse(res))
            .then((_res: any) => cb(false, "", ""))
            .catch((err: any) => {
                cb(true, "returnedError", registrationErrorMessage(err?.response?.data));
            });
    }

    loginWithToken(token: string) {
        this.setUserJWT(token);
        this.redirectTo = "/";
    }

    forgotPassword(email: string, cb: Function) {
        authAxios()
            .post("/auth/forgot_password", {
                email: email
            })
            .then(function(res: any) {
                cb(false, "", "");
            })
            .catch(function(err: any) {
                if (
                    err &&
                    err.response &&
                    err.response.data &&
                    err.response.data.message
                ) {
                    // NotificationManager.error(
                    //     "Click to Dismiss",
                    //     "Could not reset password because " +
                    //         err.response.data.message,
                    //     5000
                    // );
                    cb(true, "returnedError", err.response.data.message);
                } else {
                    // NotificationManager.error(
                    //     "Click to Dismiss",
                    //     "Could not reset password because of a server error.  Try again soon.",
                    //     5000
                    // );
                    cb(true, "returnedError", "Server Error");
                }
            });
    }

    passwordReset(
        password: string,
        confirm_password: string,
        resetToken: string,
        cb: Function
    ) {
        if (password !== confirm_password) return cb(true, "confirmPasswordError", "Password's don't match.");
        authAxios()
            .post("/auth/reset_password", {
                password: password,
                password_confirmation: confirm_password,
                token: resetToken
            })
            .then(function(res: any) {
                cb(false, "", "");
            })
            .catch(function(err: any) {
                if (
                    err &&
                    err.response &&
                    err.response.data &&
                    err.response.data.message
                ) {
                    // NotificationManager.error(
                    //     "Click to Dismiss",
                    //     "Could not reset password because " +
                    //         err.response.data.message,
                    //     5000
                    // );
                    cb(true, "returnedError", err.response.data.message);
                } else {
                    // NotificationManager.error(
                    //     "Click to Dismiss",
                    //     "Could not reset password because of a server error.  Try again soon.",
                    //     5000
                    // );
                    cb(true, "returnedError", "Server Error");
                }
            });
    }

    processUserResponse(res: any) {
        let store = this;
        return new Promise<void>((resolve, _reject) => {
            runInAction(() => {
                store.newProfileObject(res.user);
                store.loginWithToken(res.token);
            });
            resolve();
        });
    }

    login(email: string, password: string, cb: Function) {
        const store = this;
        var axiosReturn = authAxios()
            .post("/auth/login", {
                email: email,
                password: password
            })
            .then((res: any) => res.data)
            .then((res: any) => store.processUserResponse(res))
            .then((_es: any) => cb(true))
            .catch(function(_err: any) {
                cb(false);
            });
    }

    loginAsGuest() {
        var store = this;

        axios()
            .post("/login_as_guest", {})
            .then((res: any) => res.data)
            .then((res: any) => store.processUserResponse(res))
            .then(function(res: any) {
                // NotificationManager.success(
                //     "Click to Dismiss",
                //     `You are now logged in as ${res.user.profile.name}`,
                //     5000
                // );
            })
            .catch(function(err: any) {
                // NotificationManager.error(
                //     "Click to Dismiss",
                //     "Sorry, there was a problem logging in as guest, please try again.",
                //     5000
                // );
            });
    }

    authTokenSet() {
        return this.profile.auth_token_set;
    }

    @computed
    get isLoggedIn() {
        return this.profile.logged_in_user;
    }

    logout(cb: Function) {
        const store = this;

        this.status = "pending";
        authAxios()
            .post("/auth/logout", {})
            .then(function(res: any) {
                if (store.localStorage.getItem("user") == "") {
                    throw "User not logged in, not logging out.";
                }
                store.localStorage.setItem("user", "");
                store.profile = new Profile("", 0, "", "", "");
                store.rootStore.uiStore.setFlash("User has been logged out");
                store.status = "done";
                cb(true);
            })
            .catch(function(err: any) {
                store.status = "error";
                // NotificationManager.error(
                //     "Click to Dismiss",
                //     "Sorry, couldn't logout because " + err,
                //     5000
                // );
                cb(false);
            });
    }

    @action
    add(g: Profile) {
        this.profiles.push(g);
    }
}
